import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, LOCAL_STORAGE_KEY, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { selectHasLoans } from "features/preferredLanguageSlice";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
}

const LAST_ACKNOWLEDGED_LANGUAGE_KEY = "lastAcknowledgedLanguage";

const isSupportedLanguage = (value: string): boolean => {
  return SUPPORTED_LANGUAGES.includes(value);
};

const setLocalStorage = (key: string, value: string): void => {
  window.localStorage.setItem(key, value);
};

const getLocalStorage = (key: string): string | null => {
  return window.localStorage.getItem(key);
};

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const getInitialLanguage = (): string => {
  const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);

  if (savedLanguage && isSupportedLanguage(savedLanguage)) {
    return savedLanguage;
  }

  setLocalStorage(LOCAL_STORAGE_KEY, DEFAULT_LANGUAGE);
  return DEFAULT_LANGUAGE;
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false }) => {
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [userLanguage, setUserLanguage] = useState<string>(getInitialLanguage);
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const hasLoans = useSelector(selectHasLoans);

  useEffect(() => {
    const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
    if (savedLanguage === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  }, []);

  if (!featureFlag?.preferredLanguageEnabled) {
    return null;
  }

  const applyLanguageChange = (value: string) => {
    setUserLanguage(value);
    setLocalStorage(LOCAL_STORAGE_KEY, value);
    setLocalStorage(LAST_ACKNOWLEDGED_LANGUAGE_KEY, value);
    if (value === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  };

  const handleLanguageChange = (value: string) => {
    // If changing to Spanish from English, show acknowledgment modal
    if (value === "Español" && userLanguage !== "Español") {
      setPendingLanguage(value);
      setShowAcknowledgmentModal(true);
      return;
    }

    // If changing to English from Spanish, show revert confirmation modal
    if (value === DEFAULT_LANGUAGE && userLanguage !== DEFAULT_LANGUAGE) {
      setShowEnglishRevertModal(true);
      return;
    }

    applyLanguageChange(value);
  };

  const handleAcknowledge = () => {
    if (pendingLanguage) {
      applyLanguageChange(pendingLanguage);
      setPendingLanguage(null);
    }
    setShowAcknowledgmentModal(false);
    window.location.reload();
  };

  const handleRevertConfirm = () => {
    setShowEnglishRevertModal(false);
    applyLanguageChange(DEFAULT_LANGUAGE);
  };

  const handleRevertCancel = () => {
    setShowEnglishRevertModal(false);
  };

  return (
    <>
      <div className="preferred-language">
        <LanguageToggle value={userLanguage} onChange={handleLanguageChange} options={SUPPORTED_LANGUAGES} isMobile={isMobile} disabled={!!hasLoans} />
      </div>
      {showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={() => {
            setShowAcknowledgmentModal(false);
            setPendingLanguage(null);
          }}
        />
      )}
      {showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
    </>
  );
};

export default PreferredLanguage;
